/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
import React from 'react';
import { Agent, CustomFeature, Permission, Project, ProjectType } from '@moderepo/mode-internal-apis';
import { getSubRouteAbsolutePath, RouteGroup, SlideOutCompProps, SubRoute } from '@moderepo/mode-app-components';
import { SlideOutPanelOptions } from '@moderepo/mode-ui-state';
import { Route } from 'react-router-dom';
import { RitzProjectRoute, RitzProjectRouteProps } from '../routes/RitzProjectRoute';
import { DEFAULT_ROUTE_PATHS } from '../routes/AppRoutes';
import { SCDeviceDashboardPage } from './SCDeviceDashboardPage';
import { SCSensorModuleDetailsPage } from './SCSensorModuleDetailsPage';
import { DeviceSettingsPage } from '../devices/DeviceSettingsPage';
import { DEFAULT_SUB_ROUTE_NAME, ProjectTypeHandlersManager, ProjectTypePageLayoutHandler, ProjectTypeRoutesHandler, ProjectTypeSlideOutCompHandler }
    from '../../utils/ritzExtensionUtils';
import { SCSensorModulePreviewContainer } from './SCSensorModulePreviewContainer';
import { SCHomeSensorModulesPage } from './SCHomeSensorModulesPage';
import { SensorModuleContainer } from './SCUtils';
import { SCPageInfoBarContainer, SCPageInfoBarContainerProps } from './SCPageInfoBarContainer';


/**
 * Route names specific to Sensor cloud project
 */
export enum SC_SUB_ROUTE_NAME {
    SENSOR_MODULES = 'sensorModules',
}



export enum SCSlideOutPanelCompType {
    SENSOR_MODULE_PREVIEW = 'sensor_module_preview',
}


/**
 * The options required for showing preview of a Robot Definition in the Slide Out panel
 */
export interface SCSensorModulePreviewCompOptions extends SlideOutPanelOptions {
    readonly compType: SCSlideOutPanelCompType.SENSOR_MODULE_PREVIEW;
    readonly compProps: {
        readonly data: {
            readonly projectId: number;
            readonly smContainer: SensorModuleContainer;
            readonly smContainerId: number;                   // Either a homeId or deviceId, depending on the container
            readonly moduleId: string;
        };
        readonly context?: {
            readonly userId?: number|undefined;
            readonly homeId?: number|undefined;
            readonly deviceId?: number|undefined;
        } | undefined;
    }
}


/**
 * Type guard check to make sure the provided options is the options for SCSensorModulePreviewCompOptions
 * @param options
 */
export const isSensorModulePreviewCompOptions = (options: unknown): options is SCSensorModulePreviewCompOptions => {
    return (options as SlideOutPanelOptions)?.compType === SCSlideOutPanelCompType.SENSOR_MODULE_PREVIEW;
};



/**
 * Get the route path config for home details page. Because there can be multiple paths that show home details therefore we will make a function
 * that return the config can call it when we need it. This way if we need to change the config, we only need to change 1 place
 *
 * @param rootPath - The path of the route.
 */
const getHomeDetailsPageRouteGroup = (rootPath: string): RouteGroup => {
    return {
        rootPath,
        subRoutes: [
            {
                name        : DEFAULT_SUB_ROUTE_NAME.DEFAULT,
                relativePath: '',
                label       : 'label.overview',
            } as SubRoute,
            {
                name        : SC_SUB_ROUTE_NAME.SENSOR_MODULES,
                relativePath: '/sensor-modules',
                label       : 'label.sensor_module_other',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.HOME_MEMBERS,
                relativePath: '/home-members',
                label       : 'terms.member_other',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.DEVICES,
                relativePath: '/devices',
                label       : 'terms.device_other',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.TSDB,
                relativePath: '/tsdb',
                label       : 'label.tsdb',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.VIDEOS,
                relativePath: '/videos',
                label       : 'label.video_other',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.SETTINGS,
                relativePath: '/settings',
                label       : 'label.settings',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.LOGS,
                relativePath: '/logs',
                label       : 'label.logs',
            } as SubRoute,
        ],
    };
};



/**
 * Get the route path config for device details page. Because there are 2 paths that show device details
 *      - /projects/12/devices/78                       // without user/home context
 *      - /projects/12/users/34/home/56/devices/78      // with user/home context
 * The sub route settings for all of those paths are the same so instead of copy and paste this config, we will make a function that return
 * the config can call it when we need it. This way if we need to change the config, we only need to change 1 place
 *
 * @param rootPath - The path of the route.
 */
const getDeviceDetailsPageRouteGroup = (rootPath: string): RouteGroup => {
    return {
        rootPath,
        subRoutes: [
            {
                name        : DEFAULT_SUB_ROUTE_NAME.DASHBOARD,
                relativePath: '',
                label       : 'label.overview',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.LOGS,
                relativePath: '/logs',
                label       : 'label.logs',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.SETTINGS,
                relativePath: '/settings',
                label       : 'label.settings',
            } as SubRoute,
        ],
    };
};



export const SC_ROUTE_PATHS = {
    PROJECTS_$projectId_HOMES_$homeId                                                         : getHomeDetailsPageRouteGroup('/projects/:projectId/homes/:homeId'),
    PROJECTS_$projectId_USERS_$userId_HOMES_$homeId                                           : getHomeDetailsPageRouteGroup('/projects/:projectId/users/:userId/homes/:homeId'),
    PROJECTS_$projectId_HOMES_$homeId_SENSOR_MODULES_$moduleId                                : '/projects/:projectId/homes/:homeId/sensor-modules/:moduleId',
    PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_SENSOR_MODULES_$moduleId              : '/projects/:projectId/homes/:homeId/devices/:deviceId/sensor-modules/:moduleId',
    PROJECTS_$projectId_DEVICES_$deviceId_SENSOR_MODULES_$moduleId                            : '/projects/:projectId/devices/:deviceId/sensor-modules/:moduleId',
    PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_SENSOR_MODULES_$moduleId                  : '/projects/:projectId/users/:userId/homes/:homeId/sensor-modules/:moduleId',
    PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_SENSOR_MODULES_$moduleId: '/projects/:projectId/users/:userId/homes/:homeId/devices/:deviceId/sensor-modules/:moduleId',
    PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId                                       : getDeviceDetailsPageRouteGroup('/projects/:projectId/homes/:homeId/devices/:deviceId'),
    PROJECTS_$projectId_DEVICES_$deviceId                                                     : getDeviceDetailsPageRouteGroup('/projects/:projectId/devices/:deviceId'),
    PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId                         : getDeviceDetailsPageRouteGroup('/projects/:projectId/users/:userId/homes/:homeId/devices/:deviceId'),
};


/**
 * Helper function to create route component for the device main page. For sensor cloud project, we will be showing the SCDeviceDashboardPage.
 * This function is used for these routes
 *      /project/12/homes/34/devices/56
 *      /project/12/devices/56
 *      /project/12/user/34/homes/56/devices/78
 * @param routeGroup
 * @returns
 */
const getDeviceLandingPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, DEFAULT_SUB_ROUTE_NAME.DEFAULT)}
            element={(
                <RitzProjectRoute
                    element={<SCDeviceDashboardPage />}
                />
            )}
        />
    );
};


/**
 * Helper function to create route component for the device settings page.
 * This function is used for these routes
 *      /project/12/homes/34/devices/56/settings
 *      /project/12/devices/56/settings
 *      /project/12/user/34/homes/56/devices/78/settings
 * @param routeGroup
 * @returns
 */
const getDeviceSettingsPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, DEFAULT_SUB_ROUTE_NAME.SETTINGS)}
            element={(
                <RitzProjectRoute
                    element={<DeviceSettingsPage />}
                />
            )}
        />
    );
};


const getSensorModulesPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, SC_SUB_ROUTE_NAME.SENSOR_MODULES)}
            element={(
                <RitzProjectRoute
                    element={<SCHomeSensorModulesPage />}
                />
            )}
        />
    );
};


const getSensorModuleDetailsPageRouteComponent = (routePath: string): JSX.Element => {
    return (
        <Route
            path={routePath}
            element={(
                <RitzProjectRoute
                    element={<SCSensorModuleDetailsPage />}
                />
            )}
        />
    );
};



class SensorCloudProjectTypeHandler implements ProjectTypeRoutesHandler, ProjectTypeSlideOutCompHandler, ProjectTypePageLayoutHandler {



    /**
     * The Route paths for SensorCloud project will be the combination of the RITZ_ROUTE_PATHS and SC_ROUTE_PATHS
     */
    public getRoutePaths (project: Project | undefined, loggedInAgent: Agent | undefined) {
        return {
            ...DEFAULT_ROUTE_PATHS,
            ...SC_ROUTE_PATHS,
        };
    }

    public getRoutesComponent (project: Project | undefined, loggedInAgent: Agent | undefined): readonly JSX.Element[] {
        return [
            // Route component for Sensor Modules listing page. For example: /projects/1310/homes/1234/sensor-modules
            getSensorModulesPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId as RouteGroup),
            getSensorModulesPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId as RouteGroup),

            // Route component for Device's landing page. For example: /projects/1310/devices/1234
            getDeviceLandingPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId as RouteGroup),
            getDeviceLandingPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),
            getDeviceLandingPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),

            // Route component for Device's settings page. For example: /projects/1310/devices/1234/settings
            getDeviceSettingsPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId as RouteGroup),
            getDeviceSettingsPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),
            getDeviceSettingsPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),

            // Route component for Sensor Module's details page. For example: /projects/1310/homes/1234/sensor-modules/my_sensor_module_id
            getSensorModuleDetailsPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_SENSOR_MODULES_$moduleId),
            getSensorModuleDetailsPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_SENSOR_MODULES_$moduleId),
            getSensorModuleDetailsPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId_SENSOR_MODULES_$moduleId),
            getSensorModuleDetailsPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_SENSOR_MODULES_$moduleId),
            getSensorModuleDetailsPageRouteComponent(SC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_SENSOR_MODULES_$moduleId),
        ];
    }


    public getSlideOutComponent (options: SlideOutPanelOptions): React.ReactElement<SlideOutCompProps> | undefined {
        if (isSensorModulePreviewCompOptions(options)) {
            return (
                <SCSensorModulePreviewContainer
                    projectId={options.compProps.data.projectId}
                    smContainer={options.compProps.data.smContainer}
                    smContainerId={options.compProps.data.smContainerId}
                    moduleId={options.compProps.data.moduleId}
                    userId={options.compProps.context?.userId}
                    homeId={options.compProps.context?.homeId}
                />
            );
        }

        return undefined;
    }


    public getPageInfoComponent (): React.FC<SCPageInfoBarContainerProps> {
        return SCPageInfoBarContainer;
    }
}

// Create an instance of the SensorCloudProjectTypeHandler
const scProjectHandler = new SensorCloudProjectTypeHandler();



/**
 * Initialize the SensorCloud project handler by register the SensorCloudProjectTypeHandler with the ProjectTypeHandlersManager. Without this,
 * everything will still work but SensorCloud project will not have the SensorCloud specific features and the agent can only access the default
 * Ritz features.
 */
export const initializeSensorCloudProjectType = () => {
    ProjectTypeHandlersManager.getInstance().registerRoutesHandler(ProjectType.SENSOR_CLOUD, scProjectHandler);
    ProjectTypeHandlersManager.getInstance().registerPageLayoutHandler(ProjectType.SENSOR_CLOUD, scProjectHandler);

    // register the handler to handle the SensorCloud components that can be displayed in the Slide Out panel
    ProjectTypeHandlersManager.getInstance().registerSlideOutComponentHandler(SCSlideOutPanelCompType.SENSOR_MODULE_PREVIEW, scProjectHandler);
};
